import { Box, Button, IconButton, Stack, Typography, TypographyProps } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import { type SvgIconProps } from '@mui/material/SvgIcon';

import AlertExclamationCircleIcon from 'modules/common/components/Icons/AlertExclamationCircleIcon';
import CheckCircleIcon from 'modules/common/components/Icons/CheckCircleIcon';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';
import WarningExclamationIcon from 'modules/common/components/Icons/WarningExclamationIcon';

// Icon (24px) plus the row gap: on phones the copy fills the rest of the first row, and the action
// starts below it, aligned with the copy rather than the icon.
const ICON_COLUMN_WIDTH = 40;
// The small dismiss button plus the row gap, kept on the first row beside the copy.
const DISMISS_COLUMN_WIDTH = 44;

// The icon is held as a component rather than an element so an `icon` override picks up the
// variant's own colour, instead of each caller restating it and drifting from the banner.
const icons = {
    info: {
        icon: InformationCircleIcon,
        iconColor: 'text.primary',
        backgroundColor: 'cement.100',
        titleColor: 'cement.900',
        descriptionColor: 'text.primary',
    },
    warning: {
        icon: WarningExclamationIcon,
        iconColor: 'warning.main',
        backgroundColor: 'orange.100',
        titleColor: 'orange.900',
        descriptionColor: 'orange.900',
    },
    error: {
        icon: AlertExclamationCircleIcon,
        iconColor: 'error.main',
        backgroundColor: 'red.100',
        titleColor: 'red.900',
        descriptionColor: 'red.900',
    },
    'info-important': {
        icon: InformationCircleIcon,
        iconColor: 'purple.900',
        backgroundColor: 'purple.100',
        titleColor: 'purple.900',
        descriptionColor: 'purple.900',
    },
    success: {
        icon: CheckCircleIcon,
        iconColor: 'green.500',
        backgroundColor: 'green.100',
        titleColor: 'green.900',
        descriptionColor: 'green.900',
    },
};

// `contained` is the banner's main call to action, filled purple ("Connect account"). `outlined` is a
// secondary way out, bordered in the banner's own text colour so it reads as part of the banner
// ("Get help"). `text`, the default, is an inline link-style action ("Try again").
const actionColors = (variant: 'text' | 'contained' | 'outlined' | undefined, color: string) => {
    if (variant === 'contained') {
        return { backgroundColor: 'purple.500', color: 'common.white', '&:hover': { backgroundColor: 'purple.400' } };
    }
    if (variant === 'outlined') {
        return { color, borderColor: color, backgroundColor: 'transparent', '&:hover': { borderColor: color } };
    }
    return { color };
};

interface AttentionBannerProps {
    readonly title?: string;
    readonly description: string | React.ReactNode;
    readonly variant?: 'info' | 'warning' | 'error' | 'info-important' | 'success';
    readonly icon?: React.ComponentType<SvgIconProps>;
    readonly sx?: SxProps;
    readonly onDismiss?: () => void;
    readonly action?: {
        label: string;
        onClick: () => void;
        variant?: 'text' | 'contained' | 'outlined';
        /** Shows progress on the button while the work it started is still running. */
        loading?: boolean;
    };
    /** A quieter way out beside `action`, always a text button, e.g. "Get help" next to the fix itself. */
    readonly secondaryAction?: {
        label: string;
        onClick: () => void;
    };
    /**
     * Puts the action on its own row under the copy at every width, rather than beside it. For a banner
     * inside a narrow card, where a button beside the copy squeezes it into a sliver.
     */
    readonly stackAction?: boolean;
    /** Content below the copy, full width, e.g. a short list of the items the banner is about. */
    readonly children?: React.ReactNode;
    readonly slotProps?: {
        title?: TypographyProps;
        description?: TypographyProps;
        icon?: SvgIconProps;
    };
}

function AttentionBanner({
    title = undefined,
    description,
    variant = 'info',
    icon = undefined,
    sx = {},
    onDismiss,
    action,
    children,
    secondaryAction = undefined,
    stackAction = false,
    slotProps = {},
}: AttentionBannerProps) {
    const { icon: VariantIcon, iconColor, backgroundColor, titleColor, descriptionColor } = icons[variant];
    const Icon = icon ?? VariantIcon;
    const { title: titleProps, description: descriptionProps, icon: iconProps } = slotProps;
    const firstRowReserved = ICON_COLUMN_WIDTH + (onDismiss ? DISMISS_COLUMN_WIDTH : 0);
    const actionPlacement = {
        flexShrink: 0,
        ml: stackAction ? `${ICON_COLUMN_WIDTH}px` : { xs: `${ICON_COLUMN_WIDTH}px`, sm: 0 },
        order: stackAction ? 1 : { xs: 1, sm: 0 },
    };
    const primaryButton = action && (
        <Button
            variant={action.variant ?? 'text'}
            onClick={action.onClick}
            loading={action.loading ?? false}
            sx={{
                ...(secondaryAction ? {} : actionPlacement),
                typography: 'subtitle1',
                ...actionColors(action.variant, descriptionColor),
            }}>
            {action.label}
        </Button>
    );
    return (
        <Box
            sx={{
                alignItems: 'center',
                gap: 2,
                padding: 2.5,
                backgroundColor,
                borderRadius: 2,
                display: 'flex',
                // Phones: the action drops below the copy instead of squeezing it into a sliver. Children
                // always take a row of their own.
                flexWrap: children || stackAction ? 'wrap' : { xs: 'wrap', sm: 'nowrap' },
                ...sx,
            }}>
            <Icon {...iconProps} sx={{ flexShrink: 0, color: iconColor, ...iconProps?.sx }} />
            <Box
                sx={{
                    flex: 1,
                    minWidth: stackAction
                        ? `calc(100% - ${firstRowReserved}px)`
                        : { xs: `calc(100% - ${firstRowReserved}px)`, sm: 'auto' },
                }}>
                {title && (
                    <Typography
                        variant="bodyReducedSemiBold"
                        {...titleProps}
                        sx={{ color: titleColor, mb: 1, ...titleProps?.sx }}>
                        {title}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    {...descriptionProps}
                    sx={{ color: descriptionColor, ...descriptionProps?.sx }}>
                    {description}
                </Typography>
            </Box>
            {secondaryAction ? (
                // The pair may shrink and wrap, so two long labels stay inside a narrow banner.
                <Stack
                    direction="row"
                    sx={{
                        ...actionPlacement,
                        flexShrink: 1,
                        maxWidth: '100%',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                    {primaryButton}
                    <Button
                        variant="text"
                        onClick={secondaryAction.onClick}
                        sx={{ typography: 'subtitle1', ...actionColors('text', descriptionColor) }}>
                        {secondaryAction.label}
                    </Button>
                </Stack>
            ) : (
                primaryButton
            )}
            {onDismiss && (
                <IconButton
                    onClick={onDismiss}
                    aria-label="Dismiss banner"
                    size="small"
                    sx={{ alignSelf: 'flex-start', mt: -0.5, mr: -0.5 }}>
                    <CloseXIcon sx={{ fontSize: 20, color: descriptionColor }} />
                </IconButton>
            )}
            {children && <Box sx={{ flexBasis: '100%', order: 2 }}>{children}</Box>}
        </Box>
    );
}

export default AttentionBanner;
